import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Backdrop, CircularProgress } from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";
import SideNavLayout from "../customsidenav/sidenavlayout";
import { CalendarPanel } from "../components/calendar/CalendarPanel";
import ProviderFilter from "../landingscreen/ProviderFilter";
import { useNow } from "../landingscreen/cards/visittimeline";
import SigninTable from "./signintable";
import FilterPopup from "./popups/filterpopup";
import Filtericon from "../assets/patientcharts/mdi_filter-outline (1).svg";
import {
  STATUS_CHIPS,
  formatWait,
  inRoom,
  isExpected,
  isSignedIn,
  providersOf,
  statusMeta,
  waitMinutes,
} from "./signinmodel";
import "./signin.css";
import { getPracticeId, practiceShortName, signinSheetRules } from "../config/practice";
// 9928: v1 checkin/future/get; 7825: v1 /signin/get (signinSheetRequest.js).
import { postSigninSheet } from "./signinSheetRequest";

const todayRange = () => ({ startDate: new Date(), endDate: new Date(), key: "selection" });

/* The filter popup's extra views (9928, signinSheetRules().filterIcon): MR
   rows, the day's cancelled / checked-out visits, or everything. */
const NO_EXTRA_VIEWS = { ShowMR: false, ShowCancelled: false, ShowChecked: false, ShowAll: false };
const EXTRA_VIEW_LABELS = {
  ShowMR: "Show MR",
  ShowCancelled: "Show cancelled",
  ShowChecked: "Show checked out",
  ShowAll: "Show all",
};

const PeopleIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 19.5c.6-3.4 3-5.3 6-5.3s5.4 1.9 6 5.3" />
    <path d="M15.5 4.9a3.2 3.2 0 0 1 0 6.2M17.5 14.4c1.9.6 3.2 2.3 3.5 5.1" />
  </svg>
);
const ClockIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);
const DoorIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 20.5V4.5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v16" />
    <path d="M4 20.5h16M14.5 12.5v.5" />
  </svg>
);
const CheckIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
  </svg>
);
const SearchIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </svg>
);
const Chevron = ({ dir }) => (
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <polyline points={dir === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
  </svg>
);

const Kpi = ({ icon, tone, value, warn, sub }) => (
  <div className="bmsi-kpi">
    <div className={`bmsi-kpi-icon ${tone}`}>{icon}</div>
    <div className="bmsi-kpi-copy">
      <div className={`bmsi-kpi-value${warn ? " warn" : ""}`}>{value}</div>
      <div className="bmsi-kpi-sub">{sub}</div>
    </div>
  </div>
);

// Sign-in sheet: who is in the building, how long they have waited, and
// where each visit stands.
const SearchGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const SigninTab = () => {
  const practiseid = getPracticeId();
  const [selectedDateRange, setSelectedDateRange] = useState(todayRange);
  const dateRangeRef = useRef(selectedDateRange);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  // Which end of the range the calendar sets next: a day picked for "From"
  // is a single day (the common case); "To" extends it into a range.
  const [rangeField, setRangeField] = useState("from");
  const calendarRef = useRef(null);
  const showdate = useRef();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providersdata, setProvidersData] = useState([]);
  // "Signed in" shows the patients in the building; "All appointments" the
  // whole day, including the expected, checked-out, cancelled, no-show and
  // rescheduled visits (the desk's fallback when kiosk check-in fails, and
  // where the encounter form is pre-printed).
  const [showAll, setShowAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [providerFilter, setProviderFilter] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterPopup, setIsFilterPopup] = useState(false);
  const [checked, setChecked] = useState(NO_EXTRA_VIEWS);
  const now = useNow();
  // OSA batch 6 (7825): statuses its sheet leaves out (Tele - Confirmed:
  // remote patients are not on the in-office sheet) and no filter popup.
  // 9928: none / the filter icon, as before.
  const sheetRules = signinSheetRules();

  const fetchdata = async ({ dt = "", load = true }) => {
    const dateRange = dt || selectedDateRange;
    const formattedStartDate = dayjs(dateRange.startDate).format("YYYY-MM-DD");
    const formattedEndDate = dayjs(dateRange.endDate).format("YYYY-MM-DD");
    showdate.current = formattedStartDate;
    try {
      if (load) setLoading(true);
      const response = await postSigninSheet(practiseid, {
        stdate: formattedStartDate,
        eddate: formattedEndDate,
      });
      setData(response.status === 200 ? response.data : []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${practiceShortName()} - Sign-in`;
    fetchdata({});
  }, []);

  useEffect(() => {
    dateRangeRef.current = selectedDateRange;
  }, [selectedDateRange]);

  // Merge the 2 s poll into the rows in place, touching only changed rows.
  const mergeUpdatedRows = (serverData) => {
    setData((prev) => {
      const map = new Map(prev.map((row) => [row.appointmentid, row]));
      serverData.forEach((serverRow) => {
        const existing = map.get(serverRow.appointmentid);
        if (!existing) {
          map.set(serverRow.appointmentid, serverRow);
        } else if (JSON.stringify(existing) !== JSON.stringify(serverRow)) {
          map.set(serverRow.appointmentid, { ...existing, ...serverRow });
        }
      });
      return Array.from(map.values());
    });
  };

  const backgroundFetch = async () => {
    try {
      const range = dateRangeRef.current;
      const response = await postSigninSheet(practiseid, {
        stdate: dayjs(range.startDate).format("YYYY-MM-DD"),
        eddate: dayjs(range.endDate).format("YYYY-MM-DD"),
      });
      if (response.status === 200) mergeUpdatedRows(response.data);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    const interval = setInterval(backgroundFetch, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;
    const onDown = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isCalendarOpen]);

  const applyRange = (range) => {
    setSelectedDateRange(range);
    fetchdata({ dt: range });
    setIsCalendarOpen(false);
  };
  const stepDay = (delta) => {
    const day = dayjs(selectedDateRange.startDate).add(delta, "day").toDate();
    applyRange({ startDate: day, endDate: day, key: "selection" });
  };
  const openCalendar = () => {
    setRangeField("from");
    setIsCalendarOpen((v) => !v);
  };
  const pickDay = (d) => {
    const day = d.toDate();
    if (rangeField === "to") {
      applyRange({ startDate: selectedDateRange.startDate, endDate: day, key: "selection" });
    } else {
      applyRange({ startDate: day, endDate: day, key: "selection" });
    }
  };

  const start = dayjs(selectedDateRange.startDate);
  const end = dayjs(selectedDateRange.endDate);
  const singleDay = start.isSame(end, "day");
  const isToday = singleDay && start.isSame(dayjs(), "day");

  // One entry per provider, from the loaded rows only (never a hardcoded
  // name: the same build serves every practice).
  const providerOptions = useMemo(
    () => Array.from(new Set(data.flatMap(providersOf))).sort((a, b) => a.localeCompare(b)),
    [data],
  );

  // Rows after the provider filter; the tiles and chip counts follow it.
  const scoped = useMemo(
    () =>
      providerFilter.length
        ? data.filter((r) => providersOf(r).some((p) => providerFilter.includes(p)))
        : data,
    [data, providerFilter],
  );
  const signedIn = useMemo(
    () => scoped.filter((r) => isSignedIn(r) && !sheetRules.excludeStatuses.includes(r.visit_confirmation)),
    [scoped, sheetRules.excludeStatuses],
  );
  // What the sheet lists before the status chips: the signed-in patients,
  // the whole day (toggle or the popup's Show all), or the popup's MR /
  // cancelled / checked-out views.
  const extraView = checked.ShowMR || checked.ShowCancelled || checked.ShowChecked;
  const base = useMemo(() => {
    if (showAll || checked.ShowAll) return scoped;
    if (extraView) {
      return scoped.filter(
        (r) =>
          (checked.ShowMR && r.visit_confirmation === "MR") ||
          (checked.ShowCancelled && r.visit_confirmation === "Cancelled") ||
          (checked.ShowChecked && r.visit_confirmation === "Checked Out"),
      );
    }
    return signedIn;
  }, [scoped, signedIn, showAll, checked, extraView]);

  // "Checked out" counts and lists the day's checked-out visits in either
  // view; the other chips narrow whatever the view shows.
  const chipSource = (status) => (status === "Checked Out" ? scoped : base);
  const chipCounts = useMemo(() => {
    const counts = {};
    STATUS_CHIPS.forEach((s) => {
      counts[s] = chipSource(s).filter((r) => r.visit_confirmation === s).length;
    });
    return counts;
  }, [scoped, base]);
  // Patient name or #id, as the old search box matched.
  const query = searchTerm.trim().toLowerCase();
  const matchesSearch = (r) =>
    !query ||
    `${r.firstname} ${r.lastname}`.toLowerCase().includes(query) ||
    String(r.patientid).toLowerCase().includes(query);
  const rows = (statusFilter
    ? chipSource(statusFilter).filter((r) => r.visit_confirmation === statusFilter)
    : base
  ).filter(matchesSearch);

  const activeExtraViews = Object.keys(EXTRA_VIEW_LABELS).filter((k) => checked[k]);
  const anyFilter = Boolean(statusFilter || providerFilter.length || query || activeExtraViews.length);
  const clearFilters = () => {
    setStatusFilter(null);
    setProviderFilter([]);
    setSearchTerm("");
    setChecked(NO_EXTRA_VIEWS);
  };

  // Waits only mean something for today; a past day would read in days.
  const waits = isToday
    ? signedIn.map((r) => waitMinutes(r, now)).filter((m) => m != null)
    : [];
  const avgWait = waits.length
    ? Math.round(waits.reduce((sum, m) => sum + m, 0) / waits.length)
    : null;
  const overHour = waits.filter((m) => m >= 60).length;
  const expected = scoped.filter(isExpected).length;
  const roomed = signedIn.filter(inRoom).length;
  const readyCheckout = signedIn.filter((r) => r.visit_confirmation === "Ready for Checkout").length;

  return (
    <>
      <div className="h-full">
        <SideNavLayout>
          <div className="bmsi w-full">
            <div className="bmsi-kpis">
              <Kpi
                icon={<PeopleIcon />}
                tone="people"
                value={signedIn.length}
                sub={`Signed in · ${expected} still expected`}
              />
              <Kpi
                icon={<ClockIcon />}
                tone="clock"
                value={avgWait == null ? "—" : formatWait(avgWait)}
                warn={avgWait != null && avgWait >= 60}
                sub={`Average wait · ${overHour} over 60 min`}
              />
              <Kpi icon={<DoorIcon />} tone="door" value={roomed} sub="In a room" />
              <Kpi icon={<CheckIcon />} tone="check" value={readyCheckout} sub="Ready for checkout" />
            </div>

            <div className="bmsi-toolbar">
              <label className="bmsi-search">
                <SearchGlyph />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search patient name or record #"
                  aria-label="Search patients"
                />
                {searchTerm && (
                  <button type="button" className="bmsi-search-x" onClick={() => setSearchTerm("")} aria-label="Clear search">
                    ×
                  </button>
                )}
              </label>

              <ProviderFilter
                options={providerOptions}
                selected={providerFilter}
                setSelected={setProviderFilter}
              />

              <label className="bmsi-switch">
                <input
                  type="checkbox"
                  role="switch"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                />
                <span className="bmsi-switch-track" aria-hidden="true" />
                <span title="All appointments">All appts</span>
              </label>

              {/* 9928's filter popup: status / provider plus the MR,
                  cancelled, checked-out and show-all views. */}
              {sheetRules.filterIcon && (
                <button
                  type="button"
                  className={`bmsi-iconbtn${activeExtraViews.length ? " on" : ""}`}
                  title="More filters"
                  aria-label="More filters"
                  onClick={() => setIsFilterPopup((v) => !v)}
                >
                  <img src={Filtericon} alt="$/" />
                </button>
              )}

            </div>

            <div className="bmsi-row2">
            <div className="bmsi-chips">
              <button
                type="button"
                className={`bmsi-chip all${statusFilter ? "" : " on"}`}
                aria-pressed={!statusFilter}
                onClick={() => setStatusFilter(null)}
              >
                All
                <span className="bmsi-chip-n">{base.length}</span>
              </button>
              {STATUS_CHIPS.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`bmsi-chip tone-${statusMeta(status).tone}${
                    statusFilter === status ? " on" : ""
                  }`}
                  aria-pressed={statusFilter === status}
                  onClick={() => setStatusFilter(statusFilter === status ? null : status)}
                >
                  <span className="bmsi-chip-dot" />
                  {statusMeta(status).label}
                  <span className="bmsi-chip-n">{chipCounts[status]}</span>
                </button>
              ))}
              {/* Views picked in the filter popup; a click removes one. */}
              {activeExtraViews.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="bmsi-chip view on"
                  title="Remove this filter"
                  onClick={() => setChecked((prev) => ({ ...prev, [key]: false }))}
                >
                  {EXTRA_VIEW_LABELS[key]}
                  <span className="bmsi-chip-x" aria-hidden="true">×</span>
                </button>
              ))}
              {anyFilter && (
                <button type="button" className="bmsi-clear" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>

              <div className="bmsi-date" ref={calendarRef}>
                <button
                  type="button"
                  className="bmsi-date-step"
                  title="Previous day"
                  onClick={() => stepDay(-1)}
                >
                  <Chevron dir="left" />
                </button>
                <button
                  type="button"
                  className="bmsi-date-mid"
                  title="Pick a date or range"
                  onClick={openCalendar}
                >
                  <span className="bmsi-date-cap">
                    {isToday ? "Today" : singleDay ? start.format("dddd") : "Range"}
                  </span>
                  <span className="bmsi-date-val">
                    {singleDay
                      ? start.format("MM-DD-YYYY")
                      : `${start.format("MM-DD")} – ${end.format("MM-DD-YYYY")}`}
                  </span>
                </button>
                <button
                  type="button"
                  className="bmsi-date-step"
                  title="Next day"
                  onClick={() => stepDay(1)}
                >
                  <Chevron dir="right" />
                </button>
                {isCalendarOpen && (
                  <div className="bmsi-date-pop">
                    <div className="bmsi-range" role="group" aria-label="Date range">
                      <button
                        type="button"
                        className={`bmsi-range-field${rangeField === "from" ? " on" : ""}`}
                        aria-pressed={rangeField === "from"}
                        onClick={() => setRangeField("from")}
                      >
                        <span className="bmsi-range-cap">From</span>
                        <span className="bmsi-range-val">{start.format("MMM D, YYYY")}</span>
                      </button>
                      <button
                        type="button"
                        className={`bmsi-range-field${rangeField === "to" ? " on" : ""}`}
                        aria-pressed={rangeField === "to"}
                        onClick={() => setRangeField("to")}
                      >
                        <span className="bmsi-range-cap">To</span>
                        <span className="bmsi-range-val">{end.format("MMM D, YYYY")}</span>
                      </button>
                    </div>
                    <CalendarPanel
                      value={rangeField === "to" ? end : start}
                      minDate={rangeField === "to" ? start : undefined}
                      onChange={pickDay}
                    />
                    <div className="bmsi-range-hint">
                      {rangeField === "to"
                        ? `Pick the last day of the range (from ${start.format("MMM D")}).`
                        : "Pick a day, or choose To for a range of days."}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <SigninTable
              rows={rows}
              allRows={data}
              loading={loading}
              setData={setData}
              fetchdata={fetchdata}
              providersdata={providersdata}
              setProvidersData={setProvidersData}
              live={isToday}
              now={now}
            />
          </div>
        </SideNavLayout>
      </div>

      <Backdrop
        sx={{
          color: "secondary.main",
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="secondary" />
      </Backdrop>

      {sheetRules.filterIcon && (
        <FilterPopup
          isOpen={isFilterPopup}
          onClose={() => setIsFilterPopup(false)}
          checked={checked}
          parentchecked={checked}
          setChecked={(next) => setChecked({ ...NO_EXTRA_VIEWS, ...(next || {}) })}
          parentselectedstatus={statusFilter}
          parentprovider={providerFilter}
          data={scoped}
          onStatusSelect={(status) => setStatusFilter(status || null)}
          onProviderFilter={(list) => setProviderFilter(Array.isArray(list) ? list : [])}
          clearFilters={clearFilters}
        />
      )}
    </>
  );
};

export default SigninTab;
